import React from 'react'
import { useSelector } from 'react-redux'
import Row from '@amzn/meridian/row'
import Column from '@amzn/meridian/column'
import Text from '@amzn/meridian/text'
import Button from '@amzn/meridian/button'
import Icon from '@amzn/meridian/icon'
import geopinTokens from '@amzn/meridian-tokens/base/icon/geopin'
import { formatRelativeTime } from '../utils'
import { IIndividualNotificationItemProps } from '../types'
import { getSelectedLocale } from '@atredux/selectors/localeSelector'
import { _navigateToTool } from '@components/SideBar/utils/SideBarUtil'
import { getToolNameToDetailMap } from '@services/ToolMenuConfigService'
import { ITool } from '@atredux/slices/toolSlice'
import { NOTIFICATION_ICON_ALT } from '../constants'
import { useBundle } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'
import './IndividualNotificationItem.scss'

/**
 * IndividualNotificationItem Component
 *
 * Displays a single detailed notification with optional image, message,
 * location/supplementary info, and action button.
 * Responsive design adapts to mobile, tablet, and desktop screens.
 */
export const IndividualNotificationItem: React.FC<IIndividualNotificationItemProps> = ({
    toolName,
    imageUrl,
    imageRotation,
    notificationMessage,
    supplementaryMessage,
    fixtureAddress,
    timestamp,
    notificationAction,
    deepUrl,
}) => {
    const locale = useSelector(getSelectedLocale)
    const [bundle] = useBundle(BUNDLE_NAME)

    const handleActionClick = () => {
        const tool = getToolNameToDetailMap().get(toolName)
        if (tool) {
            const toolWithPath: ITool = {
                ...tool,
                hashPath: deepUrl,
            }
            _navigateToTool(toolWithPath)
        }
    }

    const relativeTime = formatRelativeTime(timestamp, bundle, locale)

    return (
        <div className="individual-notification-item" data-testid="individual-notification-item">
            <Row alignmentVertical="top" spacing="300">
                {imageUrl && (
                    <Column width="17%">
                        <div className="notification-image">
                            <img
                                src={imageUrl}
                                alt={t(bundle, NOTIFICATION_ICON_ALT)}
                                data-testid="notification-image"
                                style={imageRotation ? { transform: `rotate(${imageRotation}deg)` } : undefined}
                            />
                        </div>
                    </Column>
                )}

                <Column width={imageUrl ? '60%' : '77%'}>
                    <div className="notification-content">
                        <Text type="h100" className="notification-message">
                            {notificationMessage}
                        </Text>

                        {fixtureAddress ? (
                            <div className="supplementary-info">
                                <Icon tokens={geopinTokens} />
                                <Text type="b200" color="secondary" data-testid="fixture-address">
                                    {fixtureAddress}
                                </Text>
                            </div>
                        ) : supplementaryMessage ? (
                            <Text type="b200" color="secondary" data-testid="supplementary-message">
                                {supplementaryMessage}
                            </Text>
                        ) : null}
                    </div>
                </Column>

                <Column width="23%">
                    <div className="notification-actions primary-theme-button">
                        {/* TODO: Remove 'jwo-invisible' class once notification timestamps are supported post-Pilot launch */}
                        {/* https://app.asana.com/1/8442528107068/project/1210908264295868/task/1213923753699354?focus=true */}
                        <Text type="b100" color="secondary" className="timestamp jwo-invisible" alignment="right">
                            {relativeTime}
                        </Text>
                        <Button
                            type="primary"
                            size="small"
                            onClick={handleActionClick}
                            data-testid="action-button"
                        >
                            {notificationAction}
                        </Button>
                    </div>
                </Column>
            </Row>
        </div>
    )
}
